import { nanoid } from '#lib/util.js';

/**
 * Usage:
 * ```js
 * import { toast } from '#lib/toast.svelte.js';
 * 
 * # Basic usage
 * toast.success('Operation completed!');
 * toast.error('Something went wrong');
 * toast.info('Did you know...');
 * toast.warning('Be careful!');
 * 
 * # With options
 * toast.success('Saved!', {
 *   placement: 'top-right',
 *   duration: 3000,
 *   dismissible: true,
 *   show_close_icon: true,
 *   auto_dismiss: false         
 * });
 * ```
 */


/**
 * @typedef {Object} ToastMessage
 * @property {string} id - Unique identifier for the toast
 * @property {'success' | 'error' | 'info' | 'warning'} type - Type of toast message
 * @property {string} content - Content of the toast
 * @property {number} timestamp - Creation timestamp
 * @property {'top-left' | 'top' | 'top-right' | 'bottom-left' | 'bottom' | 'bottom-right'} placement - Screen position of the toast
 * @property {number} duration - Duration in milliseconds before auto-dismiss
 * @property {boolean} dismissible - Whether the toast can be manually dismissed
 * @property {boolean} show_close_icon - Whether the toast should show a close icon
 * @property {boolean} auto_dismiss - Whether the toast should automatically dismiss after duration
 */

class Toast {  
  /** @type {ToastMessage[]} */
  messages = $state([]);
  /** @type {HTMLElement} */
  toast_ref = $state(null);
  /** @type {Record<string, {timeoutId: number, remainingTime: number, pausedAt: number}>} */
  #timeouts = {};
  #REMOVE_ANIMATION_DURATION = 700; // matches the CSS transition

  constructor(messages) {
    this.messages = messages ?? [];
  }

  #add_message(type, content, config = {}) {
    const defaults = {
      placement: 'top',
      duration: 5000,
      dismissible: true,
      auto_dismiss: true,
      show_close_icon: false
    };

    // Generate a new toast message with default and custom config merged
    const new_message = {
      id: config.id ?? nanoid(),
      type,
      content,
      timestamp: Date.now(),
      ...defaults,
      ...config
    };

    // If a message with the same id already exists, update it rather than adding a new one.
    const existing_index = this.messages.findIndex(m => m.id === new_message.id);
    if (existing_index !== -1) {
      const existing_message = this.messages[existing_index];
      const updated_message = {
        ...existing_message,
        type,
        content,
        timestamp: Date.now(),
        updating: true,
        ...defaults,
        ...config
      };

      // Remove the updating flag after animation completes
      setTimeout(() => {
        if (this.messages[existing_index]) {
          this.messages[existing_index].updating = false;
        }
      }, 500);

      // Replace the existing message in the array.
      this.messages = [
        ...this.messages.slice(0, existing_index),
        updated_message,
        ...this.messages.slice(existing_index + 1)
      ];

      // If auto-dismiss is enabled, reset the timer.
      if (updated_message.auto_dismiss && updated_message.duration > 0) {
        if (this.#timeouts[updated_message.id]) {
          clearTimeout(this.#timeouts[updated_message.id].timeoutId);
        }
        this.#setTimer(updated_message.id, updated_message.duration);
      }
      return;
    }

    // Add the new toast message.
    this.messages = [...this.messages, new_message];

    if (new_message.auto_dismiss && new_message.duration > 0) {
      this.#setTimer(new_message.id, new_message.duration);
    }
  }

  #setTimer(id, duration) {
    this.#timeouts[id] = {
      timeoutId: setTimeout(() => this.remove(id), duration),
      remainingTime: duration,
      pausedAt: null
    };
  }

  pauseTimers(placement) {
    const now = Date.now();
    this.messages
      .filter(m => m.placement === placement)
      .forEach(message => {
        const timeout = this.#timeouts[message.id];
        if (timeout && timeout.pausedAt === null) {
          clearTimeout(timeout.timeoutId);
          timeout.remainingTime -= (now - message.timestamp);
          timeout.pausedAt = now;
        }
      });
  }

  resumeTimers(placement) {
    const now = Date.now();
    this.messages
      .filter(m => m.placement === placement)
      .forEach(message => {
        const timeout = this.#timeouts[message.id];
        if (timeout && timeout.pausedAt !== null) {
          this.#setTimer(message.id, timeout.remainingTime);
          timeout.pausedAt = null;
        }
      });
  }

  success(content, config) {
    this.#add_message('success', content, config);
  }

  error(content, config) {
    this.#add_message('error', content, config);
  }

  info(content, config) {
    this.#add_message('info', content, config);
  }

  warning(content, config) {
    this.#add_message('warning', content, config);
  }

  remove(id) {
    const message = this.messages.find(m => m.id === id);
    if (!message) return;

    message.removing = true;

    // Wait for animation to complete before removing
    setTimeout(() => {
      if (this.#timeouts[id]) {
        clearTimeout(this.#timeouts[id].timeoutId);
        delete this.#timeouts[id];
      }
      this.messages = this.messages.filter(m => m.id !== id);
    }, this.#REMOVE_ANIMATION_DURATION);
  }

  clear() {
    this.messages = [];
  }
}

export const toast = new Toast();
